import React, { useState, useEffect } from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import { ScraperJob, ScrapedPage } from "./types";

interface SharePointExportModalProps {
  job: ScraperJob;
  results: ScrapedPage[];
  onClose: () => void;
  onSuccess: (sharepointUrl: string | null, fileName: string) => void;
}

interface SharePointSite {
  id: string;
  displayName: string;
  webUrl: string;
}

export default function SharePointExportModal({
  job,
  results,
  onClose,
  onSuccess
}: SharePointExportModalProps) {
  const api = useApi();
  const [siteUrl, setSiteUrl] = useState(() => localStorage.getItem("sp_export_siteUrl") || "");
  const [folderPath, setFolderPath] = useState(() => localStorage.getItem("sp_export_folderPath") || "Velaro/Scraper");
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load SharePoint sites when modal opens
  useEffect(() => {
    loadSites();
  }, []);

  async function loadSites() {
    setLoadingSites(true);
    try {
      const res = await api.admin.get("MicrosoftGraph/Sites");
      const data = await res.json();
      setSites(Array.isArray(data) ? data : []);
    } catch {
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  }

  async function handleExport() {
    if (!siteUrl.trim()) {
      setError("Please enter a SharePoint site URL.");
      return;
    }
    setError(null);
    setExporting(true);
    try {
      // Save preferences for next time
      localStorage.setItem("sp_export_siteUrl", siteUrl.trim());
      localStorage.setItem("sp_export_folderPath", folderPath.trim() || "Velaro/Scraper");

      const pages = results.map(r => ({
        url: r.url,
        title: r.title ?? "",
        success: r.success,
        content: r.content,
        wordCount: r.word_count,
        contentType: r.content_type,
        isFaq: r.is_faq
      }));

      const res = await api.admin.post("MicrosoftGraph/ExportScraperJob", {
        jobId: job.job_id,
        jobName: job.name,
        siteUrl: siteUrl.trim(),
        folderPath: folderPath.trim() || "Velaro/Scraper",
        pages
      });
      const data = await res.json();
      if (data.success) {
        onSuccess(data.sharepointUrl, data.fileName);
      } else {
        setError(data.message || "Export failed.");
      }
    } catch {
      setError("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  const successPages = results.filter(r => r.success).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Export to SharePoint</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {job.name} — {successPages} page{successPages !== 1 ? "s" : ""} will be exported
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl">&times;</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Site picker */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              SharePoint Site URL
            </label>
            {loadingSites ? (
              <p className="text-xs text-slate-400">Loading sites...</p>
            ) : sites.length > 0 ? (
              <>
                <select
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={siteUrl}
                  onChange={e => setSiteUrl(e.target.value)}
                >
                  <option value="">— pick a site —</option>
                  {sites.map(s => (
                    <option key={s.id} value={s.webUrl}>
                      {s.displayName || s.webUrl}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mb-1">Or enter a URL manually:</p>
              </>
            ) : null}
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="https://contoso.sharepoint.com/sites/compliance"
              value={siteUrl}
              onChange={e => setSiteUrl(e.target.value)}
            />
          </div>

          {/* Folder path */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Folder Path
            </label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Velaro/Scraper"
              value={folderPath}
              onChange={e => setFolderPath(e.target.value)}
            />
            <p className="text-xs text-slate-400 mt-1">Folder inside the site's default document library. Created automatically if it doesn't exist.</p>
          </div>

          {/* What gets exported */}
          <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-800 space-y-1">
            <p className="font-medium">What gets uploaded</p>
            <ul className="list-disc list-inside space-y-0.5 mt-1">
              <li>A single JSON file with all {successPages} successfully scraped pages</li>
              <li>File name: <code className="bg-blue-100 px-1 rounded">{job.name.replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_")}_YYYYMMDD.json</code></li>
              <li>Includes full page content, headings, FAQ pairs, and metadata</li>
            </ul>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          <SecondaryBlueButton label="Cancel" onClick={onClose} disabled={exporting} />
          <PrimaryBlueButton
            label={exporting ? "Exporting..." : "Export to SharePoint"}
            onClick={handleExport}
            disabled={exporting}
          />
        </div>
      </div>
    </div>
  );
}
